<template>
  <div
    :ref="highlighted ? 'highlightedComment' : undefined"
    class="comment-item"
    :class="{ 'comment-item--highlighted': highlighted }"
  >
    <v-avatar
      :size="32"
      class="comment-item__avatar"
      style="cursor: pointer"
      @click="navigateToProfile"
    >
      <v-img v-if="displayAvatar" :src="displayAvatar" :alt="displayName" />
      <v-icon v-else>mdi-account-circle</v-icon>
    </v-avatar>

    <div class="comment-item__content">
      <div class="comment-item__header">
        <div class="comment-item__user-info">
          <div class="comment-item__name-row">
            <button class="comment-item__name" @click="navigateToProfile">
              {{ displayName }}
            </button>
            <FeaturedBadge
              v-if="featuredBadge"
              :badge="featuredBadge"
              :size="12"
              class="comment-item__featured-badge"
            />
          </div>
          <button class="comment-item__username" @click="navigateToProfile">
            @{{ username }}
          </button>
        </div>
        <span class="comment-item__timestamp">{{ formattedTime }}</span>
      </div>

      <div class="comment-item__body" v-html="formattedBody"></div>

      <div v-if="commentImages.length > 0" class="comment-item__images">
        <img
          v-for="(img, idx) in commentImages"
          :key="idx"
          :src="img.url"
          class="comment-item__image"
          @click="openImage(img.url)"
        />
      </div>

      <div class="comment-item__actions">
        <ReactionPicker
          v-if="comment.ulid"
          :post-ulid="comment.ulid"
          :current-reaction="currentReaction"
          :reaction-count="reactionCount"
          @reaction-change="handleReactionChange"
        />

        <v-btn
          icon
          size="small"
          variant="text"
          @click="$emit('reply', comment)"
        >
          <v-icon>mdi-reply</v-icon>
        </v-btn>

        <v-btn
          v-if="isOwner"
          icon
          size="small"
          variant="text"
          color="error"
          class="comment-item__delete-btn"
          @click="$emit('delete', comment)"
        >
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>

        <!-- Report button (only for non-owners) -->
        <v-btn
          v-if="!isOwner"
          icon
          size="small"
          variant="text"
          class="comment-item__report-btn"
          @click="showReportModal = true"
        >
          <v-icon>mdi-flag-outline</v-icon>
        </v-btn>
      </div>

    <!-- Report Modal -->
    <v-dialog v-model="showReportModal" max-width="400">
      <v-card class="report-dialog">
        <v-card-title class="report-dialog__title">
          Report this comment
        </v-card-title>
        <v-card-text>
          <p class="report-dialog__subtitle">Why are you reporting this comment?</p>
          <v-radio-group v-model="reportReason" class="report-dialog__options">
            <v-radio label="Spam" value="spam" />
            <v-radio label="Harassment or abuse" value="harassment" />
            <v-radio label="Hateful content" value="hate" />
            <v-radio label="Violence or dangerous content" value="violence" />
            <v-radio label="Other" value="other" />
          </v-radio-group>
          <v-textarea
            v-if="reportReason === 'other'"
            v-model="reportDetails"
            label="Please describe the issue"
            rows="3"
            maxlength="500"
            counter
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="report-dialog__actions">
          <v-spacer />
          <v-btn variant="text" @click="closeReportModal">Cancel</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :disabled="!reportReason"
            :loading="isReporting"
            @click="submitReport"
          >
            Report
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </div>

  <!-- Show/Hide Replies Toggle -->
  <button
    v-if="replyCount > 0"
    class="comment-item__toggle-replies"
    :class="{ 'comment-item__toggle-replies--expanded': showReplies }"
    @click="showReplies = !showReplies"
  >
    <v-icon size="16">{{ showReplies ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    <span>{{ showReplies ? 'Hide' : 'Show' }} {{ replyCount }} {{ replyCount === 1 ? 'reply' : 'replies' }}</span>
  </button>

  <!-- Nested Replies -->
  <div v-if="showReplies && replyCount > 0" class="comment-item__replies">
    <CommentItem
      v-for="child in childComments"
      :key="child.ulid"
      :comment="child"
      :all-comments="allComments"
      :depth="depth + 1"
      @reply="$emit('reply', $event)"
      @delete="$emit('delete', $event)"
      @reaction-change="(comment, reaction) => $emit('reactionChange', comment, reaction)"
    />
  </div>

</template>

<script setup lang="ts">
import { ref, computed, toRef } from "vue";
import { useRouter } from "vue-router";
import { useUserProfile } from "@/composables/useUserProfile";
import { useAuth } from "@/composables/useAuth";
import { useSdk } from "@/composables/useSdk";
import { FeaturedBadge } from "@/components/badges";
import ReactionPicker from "@/components/actions/ReactionPicker.vue";
import { formatBodyWithLinks } from "@/utils/postUtils";
import type { Comment, Badge } from "@/types";

interface Props {
  comment: Comment;
  allComments?: Comment[];
  highlighted?: boolean;
  depth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
});

const showReplies = ref(false);

// Computed: child comments for this comment
const childComments = computed(() => {
  if (!props.allComments) return [];
  return props.allComments.filter(c => c.parentUlid === props.comment.ulid);
});

// Computed: number of replies
const replyCount = computed(() => childComments.value.length);

const emit = defineEmits<{
  reactionChange: [comment: Comment, reaction: string | null];
  reply: [comment: Comment];
  delete: [comment: Comment];
}>();

const router = useRouter();

// Get current user for ownership check
const { user: currentUser } = useAuth();

// Reactively fetch user profile from SDK cache
const userId = toRef(() => props.comment.userId);
const { userProfile } = useUserProfile(userId);

// Navigate to user profile
function navigateToProfile() {
  if (username.value && username.value !== "user") {
    router.push(`/profile/${username.value}/songs`);
  }
}

// Computed display values with fallbacks
const displayName = computed(() => {
  return (
    userProfile.value?.displayName?.trim() ||
    userProfile.value?.name?.trim() ||
    userProfile.value?.username ||
    "Anonymous"
  );
});

const username = computed(() => {
  return userProfile.value?.username || "user";
});

const displayAvatar = computed(() => {
  return userProfile.value?.avatarUrl || userProfile.value?.avatar;
});

// Featured badge from user profile
const featuredBadge = computed((): Badge | null => {
  const profile = userProfile.value as Record<string, unknown> | null;
  return (profile?.featuredBadge as Badge | null) ?? null;
});

// Comment images
const commentImages = computed(() => {
  return (props.comment.images ?? []).filter((img) => img.url);
});

function openImage(url: string) {
  window.open(url, "_blank");
}

// Reaction state - matching PostCardActions pattern
const currentReaction = computed(() => {
  return props.comment.postEngagement?.userReaction ?? null;
});

// Calculate total reactions from reactionCounts object
const reactionCount = computed(() => {
  const reactionCounts = props.comment.postEngagement?.reactionCounts;
  if (!reactionCounts) return 0;
  return Object.values(reactionCounts).reduce(
    (sum, count) => sum + (count || 0),
    0
  );
});

function handleReactionChange(reaction: string | null) {
  emit("reactionChange", props.comment, reaction);
}

// Format time helper
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

const formattedTime = computed(() => {
  if (!props.comment.createdAt) return "";
  try {
    return formatTime(props.comment.createdAt);
  } catch {
    return "";
  }
});

// Format body with clickable mentions and hashtags
const formattedBody = computed(() => {
  return formatBodyWithLinks(
    props.comment.body || "",
    "comment-item__mention",
    "comment-item__hashtag",
  );
});

// Check if current user owns this comment
const isOwner = computed(() => {
  return currentUser.value?.ulid === props.comment.userId;
});

// Report state
const { sdk } = useSdk();
const showReportModal = ref(false);
const reportReason = ref<string | null>(null);
const reportDetails = ref("");
const isReporting = ref(false);

function closeReportModal() {
  showReportModal.value = false;
  reportReason.value = null;
  reportDetails.value = "";
}

async function submitReport() {
  const commentUlid = props.comment.ulid;
  if (!commentUlid || !reportReason.value || isReporting.value) return;

  isReporting.value = true;
  try {
    const details = reportReason.value === "other" ? reportDetails.value : undefined;
    await sdk.reportPost(commentUlid, reportReason.value, details);
    closeReportModal();
  } catch (err: unknown) {
    console.error("Failed to report comment:", err);
    // Check if already reported (409 conflict)
    const error = err as { status?: number };
    if (error.status === 409) {
      closeReportModal();
    }
  } finally {
    isReporting.value = false;
  }
}
</script>

<style scoped lang="scss">
.comment-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-radius: 8px;
  transition: background-color 0.3s ease;

  &--highlighted {
    background-color: rgba(var(--v-theme-primary), 0.15);
    padding: 12px;
    margin: 0 -12px;
    animation: highlight-pulse 2s ease-out;
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__name {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: color 0.2s ease;
    line-height: 1.2;

    &:hover {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }
  }

  &__featured-badge {
    flex-shrink: 0;
  }

  &__username {
    color: rgb(var(--v-theme-on-surface-variant));
    font-size: 0.875rem;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: color 0.2s ease;
    line-height: 1;
    text-align: left;

    &:hover {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }
  }

  &__timestamp {
    color: rgb(var(--v-theme-on-surface-variant));
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  &__body {
    color: rgb(var(--v-theme-on-surface));
    margin-bottom: 8px;
    word-wrap: break-word;

    :deep(.comment-item__mention),
    :deep(.comment-item__hashtag) {
      color: rgb(var(--v-theme-primary));
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__images {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__image {
    max-width: 300px;
    max-height: 200px;
    border-radius: 8px;
    object-fit: cover;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.85;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__delete-btn {
    margin-left: auto;
  }

  &__toggle-replies {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 44px;
    padding: 6px 12px;
    background: none;
    border: none;
    border-radius: 16px;
    color: rgb(var(--v-theme-primary));
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.1);
    }

    &--expanded {
      color: rgba(var(--v-theme-on-surface), 0.7);

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.05);
      }
    }

    .v-icon {
      opacity: 0.8;
    }
  }

  &__replies {
    margin-left: 44px;
    padding-left: 16px;
    border-left: 2px solid rgba(var(--v-theme-primary), 0.2);
  }

  &__report-btn {
    margin-left: auto;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }
}

.report-dialog {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);

  &__title {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    padding: 20px 24px 12px;
  }

  &__subtitle {
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-bottom: 16px;
  }

  &__options {
    :deep(.v-selection-control-group) {
      gap: 8px;
    }

    :deep(.v-label) {
      color: rgba(var(--v-theme-on-surface), 0.8);
    }
  }

  &__actions {
    padding: 12px 16px 16px;
  }
}

@keyframes highlight-pulse {
  0% {
    background-color: rgba(var(--v-theme-primary), 0.3);
  }
  100% {
    background-color: rgba(var(--v-theme-primary), 0.1);
  }
}
</style>
